import * as React from 'react';
import { RightSmallLine } from '@mingcute/react';

import { cn } from '../lib/cn';

export interface AdvancedProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Toggle label. Default: "Advanced options". */
  label?: React.ReactNode;
  /** Start expanded. Default: false. */
  defaultOpen?: boolean;
  /** Controlled open state (optional). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

/**
 * Progressive-disclosure expander — the anti-clutter primitive.
 *
 * Wrap the 3–5 always-relevant fields directly in a form, and tuck
 * power-user/occasional fields inside `<Advanced>`. Collapsed by default so
 * the common path stays clean; expands in place to reveal depth on demand.
 */
export const Advanced = React.forwardRef<HTMLDivElement, AdvancedProps>(
  (
    { label = 'Advanced options', defaultOpen = false, open: openProp, onOpenChange, children, className, ...props },
    ref,
  ) => {
    const isControlled = openProp !== undefined;
    const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
    const open = isControlled ? openProp! : internalOpen;
    const setOpen = (next: boolean) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    };
    const regionId = React.useId();

    return (
      <div ref={ref} className={cn('flex flex-col', className)} {...props}>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={regionId}
          onClick={() => setOpen(!open)}
          className={cn(
            'inline-flex w-fit items-center gap-1.5 rounded-[var(--radius-sm)] py-1 text-sm font-medium',
            'text-[var(--color-fg-secondary)] hover:text-[var(--color-fg)]',
            'focus-visible:outline-none',
          )}
        >
          <RightSmallLine
            size={14}
            className={cn('transition-transform duration-150', open && 'rotate-90')}
            aria-hidden
          />
          {label}
        </button>
        {open ? (
          <div id={regionId} className="mt-3 flex flex-col gap-4">
            {children}
          </div>
        ) : null}
      </div>
    );
  },
);
Advanced.displayName = 'Advanced';
